import FaqSection, { type FaqItem } from "@/components/ui/FaqSection";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";

/** Written for the HR, L&D and Talent teams evaluating inaraX, not for individual learners. */
const faqs: FaqItem[] = [
  {
    question: "What is inaraX?",
    answer:
      "inaraX is an enterprise AI learning platform. It assesses each employee's AI capability, builds a personalized learning path around their role and goals, and uses human experts to validate the skills that matter.",
  },
  {
    question: "Who is inaraX for?",
    answer:
      "Organizations building AI capability across their workforce. It is typically run by HR, L&D or Talent teams, and used by employees across every function and seniority level.",
  },
  {
    question: "How is inaraX different from a generic LLM or a course library?",
    answer:
      "An LLM answers the questions people think to ask, and a course library gives everyone the same content. inaraX starts from what each person already knows, maps learning to their role and your business needs, and gives your L&D team visibility into progress across the organization.",
  },
  {
    question: "How does human expert validation work?",
    answer:
      "AI gives every submission immediate feedback. Industry experts then review projects and assessments against real-world standards, so the skills you report on are verified, not just completed.",
  },
  {
    question: "Can you build programs around our organization?",
    answer:
      "Yes. We co-build content around your context, tools and goals, then inaraX personalizes it for every employee by role and seniority.",
    cta: { label: "Book a Consultation", href: BOOK_DEMO_HREF },
  },
  {
    question: "How do we measure impact?",
    answer:
      "Your L&D team gets a dashboard of progress, completion and assessment results across teams, so you can see capability gaps close over time.",
  },
  {
    question: "Where does the learning content come from, and can we trust it?",
    answer:
      "Every explanation, example and exercise is grounded in expert-reviewed material, not the open internet. When the AI tutor helps someone, it draws only on verified content.",
  },
  {
    question: "Do employees need a technical background?",
    answer:
      "No. inaraX personalizes for technical and non-technical roles alike, from engineers to marketing, finance and leadership teams.",
  },
  {
    question: "How is pricing structured?",
    answer:
      "Pricing is tailored to your organization's size and the scope of the program. Book a consultation and we'll put together a plan for your teams.",
    cta: { label: "Book a Consultation", href: BOOK_DEMO_HREF },
  },
];

/** `heading` lets a page that already has its own h1 (/faqs) word the section heading to suit. */
export default function Faq({ heading = "Frequently asked questions:" }: { heading?: string }) {
  return (
    <section
      id="faq"
      className="scroll-mt-20 py-size-xl px-margin-mobile md:px-margin-desktop bg-mkt-surface-container-low border-t border-mkt-outline"
    >
      <div className="mx-auto max-w-[1440px]">
        <FaqSection
          heading={heading}
          items={faqs}
          headingAlign="center"
          headingClassName="font-headline-lg text-[clamp(30px,3.4vw,48px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-on-surface mb-size-xl"
          cardClassName="bg-mkt-surface border border-mkt-outline-variant/30 rounded-lg overflow-hidden transition-all duration-300"
        />
      </div>
    </section>
  );
}
